// src/api/portalRefusals.js
//
// WHAT KIND OF FAILURE IS THIS? Four answers, because three of them want
// different copy and one of them is a bug on our side.
//
// `PortalError.shape` (portalApi.js) answers a narrower question: did a code
// arrive at all. That separated the nginx-HTML case from a coded refusal, which
// was REM-376's client half. It does NOT separate a code we RECOGNISE from one
// the server sent that we have never heard of -- both are `shape: "coded"` --
// and those are different situations:
//
//   refused                We know this refusal. Commit 4 gives it copy that can
//                          tell a submitter what to DO, which a server string
//                          written for generic callers cannot.
//
//   refused_unrecognized   The server refused for a stated reason we have no
//                          copy for. NOT a fall-through to generic: the server's
//                          `error` is a real sentence authored for a submitter
//                          -- "The file exceeds the 50 MB limit." -- and showing
//                          it verbatim is strictly better than "Something went
//                          wrong". This is the case that keeps a NEW server code
//                          useful on an OLD client, which matters because the UI
//                          and admin-api deploy separately.
//
//   unattributed           The app answered, in JSON, and named no reason. Route
//                          2's `{error: "Bad request", detail: "No file
//                          uploaded"}`; any 500. There is nothing to tell the
//                          submitter to do, and THIS IS ALSO A DEFECT REPORT:
//                          a refusal we authored should carry a code, so landing
//                          here means a server path we have not finished.
//
//   unreachable            Nothing the app wrote reached us -- the body did not
//                          parse as JSON. nginx's HTML 413 is the measured case
//                          (REM-376 §3.16). Different copy again, because a
//                          retry is pointless and the cause is the request
//                          itself rather than anything the submitter chose.
//
// ── WHY THE DECLARED SET IS COMPLETE RATHER THAN "THE ONES WE HAVE COPY FOR" ─
//
// Recognition and copy are different lists. This one answers "can this happen",
// and being complete is what makes the check below able to fail in the
// direction that matters: a code declared here that the SERVER CANNOT EMIT is
// dead weight or a typo, and a typo means a refusal we thought we handled
// silently renders as `refused_unrecognized` forever.
//
// scripts/docs-facts/check-doc-facts.mjs :: checkPortalRefusalCodes asserts
// exactly that, against the server tree. It does NOT require the reverse --
// a new server code lands here as `refused_unrecognized`, which is safe by
// construction -- but it lists them, so "safe" does not become "unnoticed".

/**
 * Every refusal code the portal surface can emit. Grouped by where it comes
 * from, so the check's report is readable against the server tree.
 *
 * ── A FROZEN ARRAY, NOT A FROZEN Set ────────────────────────────────────────
 *
 * `Object.freeze(new Set([...]))` LOOKS immutable and is not: freeze covers an
 * object's own properties, and a Set's contents live in an internal slot, so
 * `.add()` still works and `Object.isFrozen()` still answers true. This file
 * shipped that for one commit, with a test asserting "an allow-list a caller
 * can mutate is not one" that passed against a mutable set — the exact shape
 * feedback_tests_must_discriminate exists for.
 *
 * `Object.freeze` on an ARRAY does prevent `push`, in strict mode by throwing.
 * The Set is derived privately below for lookup, out of a caller's reach.
 */
export const PORTAL_REFUSAL_CODES = Object.freeze([
  // the upload itself — planReviewSource.js, and the multer wrapper
  "no_extension", "unsupported_format", "empty_file", "file_too_large",
  "upload_form_invalid", "upload_incomplete",
  "crs_undeclared", "unreadable_source", "no_layers", "too_many_layers",
  "unknown_sublayer", "conversion_failed", "not_a_feature_collection",

  // the staging token — scoped to the review, and it expires
  "token_expired", "token_scope_mismatch",

  // preparing features — planReviewSubmission.js
  "no_features", "unsupported_geometry", "geom_invalid",

  // tagging — the whole file is refused on one bad tag
  "tags_missing", "tag_incomplete", "tag_unknown_feature",
  "tag_ordinal_invalid", "tag_value_invalid", "tag_duplicate",

  // creation — the org resolved from user_orgs disappeared mid-request
  "org_unavailable",

  // creation against an OWNER — the submission grant, or its absence
  "owner_org_id_invalid", "no_submission_grant", "submission_grant_revoked",
  // ...and the omission. An org that has been named as an operator must say
  // where a plan is going: the default resolves to the caller's own org, which
  // is a guess for such a caller, and guessing filed LP-2026-0001 into the
  // operator's own tenancy. (No quoted words in this comment -- the checker
  // scrapes string literals from this file, and a quoted phrase reads to it as
  // a declared refusal code the server cannot emit.)
  "owner_org_id_required",

  // stage transitions — planReviewStages.js
  "not_awaiting_submission", "not_awaiting_review", "not_open_for_markup",
  "nothing_to_return", "review_approved", "no_current_revision",

  // markup — the reviewer's side, and freezing
  "markup_type_unknown", "note_too_short", "markup_not_found",
  "not_the_markup_author", "markup_frozen", "unfrozen_markup",
  "feature_id_invalid", "feature_not_on_current_revision",

  // notes — the thread
  "note_body_required", "note_body_too_long",
  "parent_id_invalid", "parent_is_a_reply", "parent_not_in_thread",
  "bad_cursor",

  // export
  "unsupported_export_format", "revision_not_exportable", "export_not_audited",

  // the confinement gate — middleware/restrictedAccount.js. NOT from the portal
  // router, but a portal client is the only thing that can receive it, and it is
  // the one refusal that means "this account may not be here at all".
  "restricted_account",
]);

/** Private, so nothing can add to what the predicate below consults. */
const CODE_SET = new Set(PORTAL_REFUSAL_CODES);

/**
 * Is this a refusal code the portal surface can produce?
 *
 * A Set lookup, not an object-literal one. `{}["constructor"]` is truthy, and
 * this codebase has shipped that twice — caseStages.js, then isAllowedPath,
 * where it was a 500 where a 403 belonged (REM-367).
 */
export function isPortalRefusalCode(code) {
  return typeof code === "string" && CODE_SET.has(code);
}

/**
 * @typedef {"refused"|"refused_unrecognized"|"unattributed"|"unreachable"} Disposition
 */

/**
 * Classify a failure. Takes the PortalError, not a status or a code, because
 * the answer depends on BOTH whether a code arrived and whether we know it --
 * and a caller passing just `err.code` cannot express "no body at all".
 *
 * @param {{shape?: string, code?: string|null}} err
 * @returns {Disposition}
 */
export function dispositionFor(err) {
  // Order matters: `unreachable` first, because a non-JSON body has no code and
  // would otherwise be indistinguishable from `unattributed` below.
  if (err?.shape === "non_json") return "unreachable";
  const code = err?.code;
  if (typeof code !== "string" || code.length === 0) return "unattributed";
  return isPortalRefusalCode(code) ? "refused" : "refused_unrecognized";
}

/**
 * The text to show when there is no copy of our own -- used by
 * `refused_unrecognized`, where the server's sentence is the best thing we have.
 *
 * Returns null rather than a placeholder when there is nothing usable, so a
 * caller must decide what to render instead of accidentally showing "undefined"
 * or an empty bubble.
 */
export function serverStatedReason(err) {
  const msg = err?.payload?.error;
  return typeof msg === "string" && msg.trim().length > 0 ? msg.trim() : null;
}
